import _get from 'lodash-es/get';
import {jwtDecode} from 'jwt-decode';

import {AuthenticationState, AuthenticationActions} from '@common/types/stores/authenticationStore.types';
import {JwtScope} from '@common/types/authentication/authentication.types';
import {apiAuthenticate, apiRefreshJwt, apiBuildScopedJwt} from '@common/api/apiAuthentication';
import {persistMainJwtToStorage, persistScopedJwtToStorage, extractStsCredentialsFromMainJwt} from '@common/helpers/authentication/authenticationHelper';
import {getEndPoints} from '@common/stores/configStore';
import {createCustomStore} from '@common/helpers/store/storeHelper';

export const initialState: AuthenticationState = {
  isAuthenticated: false,
  mainJwt: '',
  roles: [],
  scopedJwts: {},
  stsCredentials: {
    accessKeyId: '',
    secretAccessKey: '',
    sessionToken: undefined
  },
  user: {}
};

const useAuthenticationStore = createCustomStore<AuthenticationState & AuthenticationActions>('authentication', (set, get) => ({
  ...initialState,

  authenticate: () => {
    const endpoints = getEndPoints();
    return apiAuthenticate(endpoints.apiWebApp);
  },

  finaliseAuthenticate: (mainJwt: string) => {
    persistMainJwtToStorage(mainJwt);

    return set(
      state => {
        state.mainJwt = mainJwt;
        state.isAuthenticated = true;
        state.stsCredentials = extractStsCredentialsFromMainJwt(mainJwt);
      },
      undefined,
      'finaliseAuthenticate'
    );
  },

  buildScopedJwt: async (scope: JwtScope['scope'], assets: JwtScope['assets'], withStatic: JwtScope['withStatic']) => {
    const endpoints = getEndPoints();
    const state = get();
    const {mainJwt, stsCredentials} = state;
    const {token: scopedJwt} = await apiBuildScopedJwt(endpoints.apiWebApp, stsCredentials, mainJwt, {
      scope,
      assets,
      withStatic
    });
    persistScopedJwtToStorage(scope, scopedJwt);
    const decodedJwt = jwtDecode(scopedJwt);

    set(
      state => {
        state.user = _get(decodedJwt, 'user', {});
        state.roles = _get(decodedJwt, 'roles', []);
        state.scopedJwts[scope] = scopedJwt;
      },
      undefined,
      'buildScopedJwt'
    );
  },

  setScopedJwt: async (scopedJwt: string, scope: string | number) => {
    const decodedJwt = jwtDecode(scopedJwt);

    set(
      state => {
        state.user = _get(decodedJwt, 'user', {});
        state.roles = _get(decodedJwt, 'roles', []);
        state.scopedJwts[scope] = scopedJwt;
      },
      undefined,
      'setScopedJwt'
    );
  },

  refreshMainJwt: async () => {
    const endpoints = getEndPoints();
    const state = get();
    const {mainJwt, stsCredentials} = state;
    const {token: newMainJwt} = await apiRefreshJwt(endpoints.apiWebApp, stsCredentials, mainJwt);
    persistMainJwtToStorage(newMainJwt);

    set(
      state => {
        state.mainJwt = newMainJwt;
      },
      undefined,
      'refreshMainJwt'
    );
  },

  refreshScopedJwt: async (scope: JwtScope['scope']) => {
    const endpoints = getEndPoints();
    const state = get();
    const {mainJwt, stsCredentials} = state;
    const {token: newScopedJwt} = await apiRefreshJwt(endpoints.apiWebApp, stsCredentials, mainJwt);
    persistScopedJwtToStorage(scope, newScopedJwt);

    set(
      state => {
        state.scopedJwts[scope] = newScopedJwt;
      },
      undefined,
      'refreshScopedJwt'
    );
  }
}));

export default useAuthenticationStore;
